import { useState, useMemo, useCallback } from "react";
import { useAppData } from "@/data/AppDataContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { OrderingModal } from "@/components/OrderingModal";
import { IntakeReviewWorkspace } from "@/components/IntakeReviewWorkspace";
import { deriveBuildReviewStatus } from "@/data/buildReviewTypes";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { BuildReviewService } from "@/services/build-review.service";
import { JobService } from "@/services/job.service";
import {
  ClipboardCheck, ShoppingCart, Package, Truck, Box, CalendarCheck,
  Clock, Phone, ChevronDown, ChevronUp, Zap, AlertTriangle,
} from "lucide-react";
import type { Job } from "@/data/types";
import { getStageConfig } from "@/data/workflowEngine";

/* ── Lane config ── */
interface PipelineLane {
  key: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  actionLabel: string;
  headerBg: string;
  headerText: string;
  borderAccent: string;
}

const LANES: PipelineLane[] = [
  { key: "build-review", label: "Build Review", shortLabel: "Review", icon: ClipboardCheck, actionLabel: "Review", headerBg: "bg-warning/10", headerText: "text-warning", borderAccent: "border-t-amber-400" },
  { key: "planning", label: "Material Planning", shortLabel: "Planning", icon: Package, actionLabel: "Confirm", headerBg: "bg-info/10", headerText: "text-info", borderAccent: "border-t-cyan-400" },
  { key: "ready-ordering", label: "Ready for Ordering", shortLabel: "Ordering", icon: ShoppingCart, actionLabel: "Order", headerBg: "bg-info/10", headerText: "text-info", borderAccent: "border-t-blue-400" },
  { key: "ordered", label: "Ordered", shortLabel: "Ordered", icon: Package, actionLabel: "", headerBg: "bg-primary/10", headerText: "text-primary", borderAccent: "border-t-indigo-400" },
  { key: "receiving", label: "Receiving", shortLabel: "Receiving", icon: Truck, actionLabel: "Confirm", headerBg: "bg-primary/10", headerText: "text-primary", borderAccent: "border-t-purple-400" },
  { key: "staged", label: "Staged", shortLabel: "Staged", icon: Box, actionLabel: "Mark Ready", headerBg: "bg-success/10", headerText: "text-success", borderAccent: "border-t-emerald-400" },
  { key: "ready-install", label: "Ready for Install", shortLabel: "Install", icon: CalendarCheck, actionLabel: "Schedule", headerBg: "bg-success/10", headerText: "text-success", borderAccent: "border-t-green-500" },
];

/* ── Lane assignment (uses workflow engine for stage lookups) ── */
function getLane(job: Job, events: BuildReviewEvent[]): string | null {
  const stageConfig = getStageConfig(job.currentStage);
  // Completed jobs don't appear in pipeline
  if (!stageConfig || job.currentStage === "Completed") return null;

  const jobEvents = events.filter(e => e.jobId === job.id);
  const isApproved = deriveBuildReviewStatus(jobEvents) === "Approved";

  // Material-based lane assignment (takes precedence)
  if (job.materialReadiness === "Loaded for Crew") return "ready-install";
  if (job.materialReadiness === "Staged for Job") return "staged";
  if (job.materialReadiness === "Fully Received") return "receiving";
  if (job.materialReadiness === "Ordered" || job.materialReadiness === "Partially Received") return "ordered";

  // Planning/ordering stage check via engine order
  if (job.planningStatus === "Ready for Ordering" || job.planningStatus === "Sent to Ordering") return "ready-ordering";
  if (isApproved && job.planningStatus === "In Planning") return "planning";

  // Early stages — use engine order to confirm pre-review
  if (stageConfig.order <= 20 && !isApproved) return "build-review";
  return null;
}

/* ── Helpers ── */
function getProductLabel(job: Job): string {
  const material = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
  const enclosure = job.projectConfig?.enclosureType || "";
  const type = job.projectType || "Shower";
  return [material, type, enclosure ? `• ${enclosure}` : ""].filter(Boolean).join(" ") || type;
}

function getDaysInStage(job: Job): number {
  const last = job.activityHistory?.[job.activityHistory.length - 1];
  if (!last?.date) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(last.date).getTime()) / 86_400_000));
}

function getPriority(job: Job): "rush" | "standard" | "flexible" {
  if (job.orderType === "Emergency" || job.issueFlag) return "rush";
  if (job.scheduledInstallDate) return "standard";
  return "flexible";
}

const PRIORITY_STYLES: Record<string, { label: string; className: string }> = {
  rush: { label: "Rush", className: "bg-destructive/10 text-destructive border-destructive/20" },
  standard: { label: "Standard", className: "bg-warning/15 text-warning border-warning/30" },
  flexible: { label: "Flexible", className: "bg-success/15 text-success border-success/30" },
};

/* ── Component ── */
export function PipelineView() {
  const { jobs, issues, setJobs, buildReviewEvents, setBuildReviewEvents } = useAppData();
  const navigate = useNavigate();
  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  const [orderingJob, setOrderingJob] = useState<Job | null>(null);
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [collapsedLanes, setCollapsedLanes] = useState<Record<string, boolean>>({});

  const openJobs = useMemo(() => jobs.filter(j => j.currentStage !== "Completed"), [jobs]);

  const laneJobs = useMemo(() => {
    const map: Record<string, Job[]> = {};
    LANES.forEach(l => { map[l.key] = []; });
    openJobs.forEach(j => {
      const lane = getLane(j, buildReviewEvents);
      if (lane && map[lane]) {
        const priority = getPriority(j);
        if (filterPriority === "all" || filterPriority === priority) {
          map[lane].push(j);
        }
      }
    });
    // Sort: rush first, then by days waiting desc
    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => {
        const pa = getPriority(a) === "rush" ? 0 : getPriority(a) === "standard" ? 1 : 2;
        const pb = getPriority(b) === "rush" ? 0 : getPriority(b) === "standard" ? 1 : 2;
        if (pa !== pb) return pa - pb;
        return getDaysInStage(b) - getDaysInStage(a);
      });
    });
    return map;
  }, [openJobs, buildReviewEvents, filterPriority]);

  const totalInPipeline = useMemo(() => Object.values(laneJobs).reduce((s, arr) => s + arr.length, 0), [laneJobs]);

  const toggleCollapse = (key: string) => setCollapsedLanes(prev => ({ ...prev, [key]: !prev[key] }));

  /* ── Action handlers (reuse existing logic) ── */
  const handleOrderingSubmit = useCallback(async (data: any) => {
    const target = jobs.find((j) => j.id === data.jobId);
    if (!target) return;

    const patch: Partial<Job> = {
      materialReadiness: "Ordered" as Job["materialReadiness"],
      planningStatus: "Ordered" as const,
      currentStage: "Ordered" as Job["currentStage"],
      selectedVendor: data.vendor,
      orderType: data.orderType === "Rush" ? "Emergency" as const : "Batch" as const,
      activityHistory: [...target.activityHistory, {
        id: `act-${Date.now()}`,
        date: new Date().toISOString().split("T")[0],
        action: `Materials ordered — ${data.vendor} (${data.orderType})`,
        user: "Super Admin",
      }],
    };

    const { data: persisted, error } = await JobService.update(target.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist ordering update:", error);
      toast.error("Failed to submit order");
      return;
    }

    setJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    setOrderingJob(null);
    toast.success("Order submitted");
  }, [jobs, setJobs]);

  const handleConfirmReceived = useCallback(async (jobId: string) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;

    const patch: Partial<Job> = {
      materialReadiness: "Fully Received" as Job["materialReadiness"],
      activityHistory: [...target.activityHistory, {
        id: `act-${Date.now()}`,
        date: new Date().toISOString().split("T")[0],
        action: "Materials confirmed received",
        user: "Super Admin",
      }],
    };
    const { data: persisted, error } = await JobService.update(target.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist received confirmation:", error);
      toast.error("Failed to confirm received");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    toast.success("Received confirmed");
  }, [jobs, setJobs]);

  const handleMarkReady = useCallback(async (jobId: string) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;

    const patch: Partial<Job> = {
      materialReadiness: "Loaded for Crew" as Job["materialReadiness"],
      installReadiness: "Ready" as any,
      schedulingStatus: "Unscheduled" as any,
      activityHistory: [...target.activityHistory, {
        id: `act-${Date.now()}`,
        date: new Date().toISOString().split("T")[0],
        action: "Marked ready for install",
        user: "Super Admin",
      }],
    };
    const { data: persisted, error } = await JobService.update(target.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist ready-for-install:", error);
      toast.error("Failed to mark ready for install");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    toast.success("Ready for install");
  }, [jobs, setJobs]);

  const handleApprove = useCallback(async (jobId: string) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;
    const lookup = target.jobNumber || target.id;
    const { data, error } = await BuildReviewService.approve(String(lookup), "ops_manager");
    if (error || !data) {
      console.error("Failed to approve build review:", error);
      toast.error("Failed to approve build review");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === data.id ? data : j)));
    setBuildReviewEvents(prev => [...prev, {
      id: `bre-${Date.now()}`,
      jobId,
      action: "approved" as const,
      timestamp: new Date().toISOString(),
      performedBy: "ops_manager",
      notes: "Approved via Pipeline View",
    }]);
    setReviewJobId(null);
    toast.success("Approved — moved to Material Planning");
  }, [jobs, setJobs, setBuildReviewEvents]);

  /** Push-Mode variant: commits approval state but keeps the workspace open
   *  so the next-step panel can render. */
  const commitApproval = useCallback(async (jobId: string) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;
    const lookup = target.jobNumber || target.id;
    const { data, error } = await BuildReviewService.approve(String(lookup), "ops_manager");
    if (error || !data) {
      console.error("Failed to approve build review:", error);
      toast.error("Failed to approve build review");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === data.id ? data : j)));
    setBuildReviewEvents(prev => [...prev, {
      id: `bre-${Date.now()}`,
      jobId,
      action: "approved" as const,
      timestamp: new Date().toISOString(),
      performedBy: "ops_manager",
      notes: "Approved via Pipeline View",
    }]);
    toast.success("Approved — moved to Material Planning");
  }, [jobs, setJobs, setBuildReviewEvents]);

  const handleRequestRevision = useCallback(async (jobId: string, notes: string, materials: string, reason: any) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;
    const lookup = target.jobNumber || target.id;
    const { error } = await BuildReviewService.requestRevision(String(lookup), "ops_manager", notes, reason, materials);
    if (error) {
      console.error("Failed to request revision:", error);
      toast.error("Failed to request revision");
      return;
    }
    setBuildReviewEvents(prev => [...prev, {
      id: `bre-${Date.now()}`,
      jobId,
      action: "revision_requested" as const,
      timestamp: new Date().toISOString(),
      performedBy: "ops_manager",
      notes,
      revisionReason: reason,
      materialChanges: materials,
    }]);
    setReviewJobId(null);
    toast.info("Revision requested");
  }, [jobs, setBuildReviewEvents]);

  const handleConfirmPlanning = useCallback(async (jobId: string) => {
    const target = jobs.find((j) => j.id === jobId);
    if (!target) return;

    const patch: Partial<Job> = {
      planningStatus: "Ready for Ordering" as const,
      currentStage: "Ready for Ordering" as Job["currentStage"],
      activityHistory: [...target.activityHistory, {
        id: `act-${Date.now()}`,
        date: new Date().toISOString().split("T")[0],
        action: "Material plan confirmed — ready for ordering",
        user: "Super Admin",
      }],
    };
    const { data: persisted, error } = await JobService.update(target.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist planning confirmation:", error);
      toast.error("Failed to confirm planning");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    toast.success("Materials confirmed — ready for ordering");
  }, [jobs, setJobs]);

  const handleAction = (job: Job, laneKey: string) => {
    switch (laneKey) {
      case "build-review": setReviewJobId(job.id); break;
      case "planning": handleConfirmPlanning(job.id); break;
      case "ready-ordering": setOrderingJob(job); break;
      case "receiving": handleConfirmReceived(job.id); break;
      case "staged": handleMarkReady(job.id); break;
    }
  };

  const reviewJob = reviewJobId ? jobs.find(j => j.id === reviewJobId) ?? null : null;
  const reviewJobEvents = reviewJobId ? buildReviewEvents.filter(e => e.jobId === reviewJobId) : [];

  return (
    <div className="space-y-3">
      {/* ── Filter bar ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">{totalInPipeline} jobs in pipeline</span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="h-7 w-[130px] text-xs">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="rush">Rush</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="flexible">Flexible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Summary KPI strip ── */}
      <div className="grid grid-cols-6 gap-1.5">
        {LANES.map(lane => {
          const count = laneJobs[lane.key]?.length || 0;
          const aging = laneJobs[lane.key]?.filter(j => getDaysInStage(j) > 3).length || 0;
          return (
            <button key={lane.key} onClick={() => toggleCollapse(lane.key)} className={`rounded-md border border-t-2 ${lane.borderAccent} ${lane.headerBg} p-2 text-center transition-colors hover:opacity-90`}>
              <p className={`text-base font-bold ${lane.headerText}`}>{count}</p>
              <p className="text-[9px] font-medium text-muted-foreground leading-tight">{lane.shortLabel}</p>
              {aging > 0 && <p className="text-[9px] text-destructive font-medium mt-0.5">{aging} aging</p>}
            </button>
          );
        })}
      </div>

      {/* ── Horizontal Kanban ── */}
      <ScrollArea className="w-full">
        <div className="flex gap-2.5 pb-3 min-w-[1080px]">
          {LANES.map(lane => {
            const jobsInLane = laneJobs[lane.key] || [];
            const isCollapsed = collapsedLanes[lane.key];

            return (
              <div key={lane.key} className="flex-1 min-w-[170px] max-w-[220px]">
                {/* Lane header */}
                <button onClick={() => toggleCollapse(lane.key)} className={`w-full flex items-center justify-between gap-1 rounded-t-md border border-b-0 border-t-2 ${lane.borderAccent} ${lane.headerBg} px-2.5 py-1.5`}>
                  <div className="flex items-center gap-1.5">
                    <lane.icon className={`h-3.5 w-3.5 ${lane.headerText}`} />
                    <span className={`text-[11px] font-semibold ${lane.headerText}`}>{lane.shortLabel}</span>
                    <Badge variant="secondary" className="h-4 px-1 text-[9px]">{jobsInLane.length}</Badge>
                  </div>
                  {isCollapsed ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronUp className="h-3 w-3 text-muted-foreground" />}
                </button>

                {/* Lane body */}
                {!isCollapsed && (
                  <div className="border border-t-0 rounded-b-md bg-muted/20 p-1.5 space-y-1.5 max-h-[calc(100vh-280px)] overflow-y-auto">
                    {jobsInLane.length === 0 && (
                      <p className="text-[10px] text-muted-foreground text-center py-4">No jobs</p>
                    )}
                    {jobsInLane.map(job => {
                      const days = getDaysInStage(job);
                      const priority = getPriority(job);
                      const isAging = days > 3;

                      return (
                        <Card key={job.id} className={`shadow-none border transition-shadow hover:shadow-sm ${isAging ? "border-destructive/30 bg-destructive/5" : ""}`}>
                          <CardContent className="p-2">
                            {/* Row 1 — Identity */}
                            <div className="flex items-center justify-between gap-1 mb-0.5">
                              <Link to={`/jobs/${job.id}`} className="text-[11px] font-bold text-primary hover:underline truncate">{job.customerName}</Link>
                              <a href={`tel:${job.phone}`} className="shrink-0 text-muted-foreground hover:text-primary">
                                <Phone className="h-3 w-3" />
                              </a>
                            </div>

                            {/* Row 2 — Product */}
                            <p className="text-[10px] text-muted-foreground truncate mb-1">{getProductLabel(job)}</p>

                            {/* Row 3 — Meta + Action */}
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex items-center gap-1 min-w-0">
                                {isAging && <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />}
                                <span className={`text-[9px] flex items-center gap-0.5 ${isAging ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                                  <Clock className="h-2.5 w-2.5" />{days}d
                                </span>
                                <span className={`text-[8px] px-1 py-0.5 rounded border ${PRIORITY_STYLES[priority].className}`}>
                                  {PRIORITY_STYLES[priority].label}
                                </span>
                              </div>
                              {lane.actionLabel && lane.key !== "ordered" && (
                                <Button size="sm" variant={priority === "rush" ? "default" : "outline"} className="h-5 px-1.5 text-[9px]" onClick={() => handleAction(job, lane.key)}>
                                  {lane.actionLabel}
                                </Button>
                              )}
                              {lane.key === "ready-install" && (
                                <Link to="/scheduling">
                                  <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px]">Schedule</Button>
                                </Link>
                              )}
                            </div>

                            {/* Optional: Sales rep / Install date */}
                            {(job.assignedCrew || job.scheduledInstallDate) && (
                              <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-border/50">
                                {job.assignedCrew && <span className="text-[9px] text-muted-foreground truncate">{job.assignedCrew}</span>}
                                {job.scheduledInstallDate && <span className="text-[9px] text-muted-foreground ml-auto">{job.scheduledInstallDate}</span>}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* ── Modals ── */}
      <IntakeReviewWorkspace
        job={reviewJob}
        open={!!reviewJob}
        buildReviewEvents={reviewJobEvents}
        openIssues={issues.filter(i => reviewJobId && i.jobId === reviewJobId)}
        onClose={() => setReviewJobId(null)}
        onApprove={handleApprove}
        onApproveAndContinue={commitApproval}
        onContinueChain={(approvedJob, step) => {
          setReviewJobId(null);
          if (step.kind === "done") return;
          if (step.kind === "generate_po") { setOrderingJob(approvedJob); return; }
          if (step.kind === "navigate")    { navigate(step.to); return; }
          navigate(`/jobs/${approvedJob.id}`);
        }}
        onRequestRevision={handleRequestRevision}
      />
      {orderingJob && (
        <OrderingModal
          open={!!orderingJob}
          onOpenChange={(o) => !o && setOrderingJob(null)}
          job={orderingJob}
          onSendToOrdering={handleOrderingSubmit}
        />
      )}
    </div>
  );
}
